import { useState, useEffect } from 'react'
import { ArrowLeft, Save, Calendar, MapPin, Youtube, Upload, Settings, DollarSign, Clock, CheckCircle, Search, X } from 'lucide-react'
import { useToast } from '../../hooks/useToast'
import Toast from '../../components/ui/Toast'
import { useOrganizador } from '../../contexts/OrganizadorContext'
import leiloesService from '../../services/leiloesService'
import organizadoresService from '../../services/organizadoresService'
import clientesService from '../../services/clientesService'
import { comissoesService } from '../../services/comissoesService'
import ComissaoBeneficenteOpcao from '../../components/leiloes/ComissaoBeneficenteOpcao'
import leiloeiroService from '../../services/leiloeiroService'
import NovaComissaoModal from '../../components/modals/NovaComissaoModal'
import NovoLeiloeiroModal from '../../components/modals/NovoLeiloeiroModal'
import EditarComissaoModal from '../../components/modals/EditarComissaoModal'
import EditarLeiloeiroModal from '../../components/modals/EditarLeiloeiroModal'
import { meiosPagamentoService } from '../../services/meiosPagamentoService'
import faixasDefesaService from '../../services/faixasDefesaService'
import FaixasDefesaEditor from '../../components/comissoes/FaixasDefesaEditor'
import NovoMeioPagamentoModal from '../../components/modals/NovoMeioPagamentoModal'
import promotoresService from '../../services/promotoresService'

// ─── Componente inline: Promotores na criação do leilão ──────────────────────
function PromotoresLeilaoNovo({ organizadorId, promotores = [], value = [], onChange, titulo = '👤 Promotores', labelItem = 'promotor' }) {
  const [showAdd, setShowAdd] = useState(false)
  const [selectedId, setSelectedId] = useState('')
  const [percVenda, setPercVenda] = useState('')
  const [percDefesa, setPercDefesa] = useState('')
  const [criandoNovo, setCriandoNovo] = useState(false)
  const [novoNome, setNovoNome] = useState('')
  const [salvandoNovo, setSalvandoNovo] = useState(false)

  const disponiveis = promotores.filter(p => !value.some(v => v.promotor_id === p.id))

  const fecharAdd = () => {
    setShowAdd(false); setCriandoNovo(false)
    setSelectedId(''); setNovoNome(''); setPercVenda(''); setPercDefesa('')
  }

  const handleAdd = async () => {
    if (criandoNovo || disponiveis.length === 0) {
      if (!novoNome.trim()) return
      setSalvandoNovo(true)
      try {
        const novo = await promotoresService.criar(organizadorId, {
          nome: novoNome.trim(),
          perc_venda: parseFloat(percVenda) || 0,
          perc_defesa: parseFloat(percDefesa) || 0,
        })
        onChange([...value, { promotor_id: novo.id, nome: novo.nome, perc_venda: parseFloat(percVenda) || novo.perc_venda, perc_defesa: parseFloat(percDefesa) || novo.perc_defesa }])
        fecharAdd()
      } catch {
        // erro silencioso é aceitável aqui — o usuário só tenta de novo
      } finally {
        setSalvandoNovo(false)
      }
      return
    }
    if (!selectedId) return
    const p = promotores.find(pr => String(pr.id) === selectedId)
    if (!p) return
    onChange([...value, { promotor_id: p.id, nome: p.nome, perc_venda: parseFloat(percVenda) || p.perc_venda, perc_defesa: parseFloat(percDefesa) || p.perc_defesa }])
    fecharAdd()
  }

  const handleRemove = (id) => onChange(value.filter(v => v.promotor_id !== id))

  const handleSelect = (id) => {
    setSelectedId(id)
    const p = promotores.find(pr => String(pr.id) === id)
    if (p) { setPercVenda(String(p.perc_venda || '')); setPercDefesa(String(p.perc_defesa || '')) }
  }

  if (!organizadorId) return null

  return (
    <div className="mt-6 p-4 bg-indigo-50 border border-indigo-200 rounded-lg space-y-3">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-semibold text-indigo-800">{titulo}</h4>
        {!showAdd && (
          <button type="button" onClick={() => { setShowAdd(true); setCriandoNovo(disponiveis.length === 0) }} className="text-xs text-indigo-600 font-medium hover:underline">
            + {disponiveis.length === 0 ? `Novo ${labelItem}` : 'Adicionar'}
          </button>
        )}
      </div>
      {value.length > 0 && (
        <div className="space-y-1.5">
          {value.map(v => (
            <div key={v.promotor_id} className="flex items-center justify-between p-2 bg-white rounded border border-indigo-100">
              <span className="text-sm font-medium text-gray-800">{v.nome}</span>
              <div className="flex items-center gap-2">
                <span className="text-xs text-indigo-700">V:{v.perc_venda}%</span>
                <span className="text-xs text-orange-600">D:{v.perc_defesa}%</span>
                <button type="button" onClick={() => handleRemove(v.promotor_id)} className="text-red-400 hover:text-red-600 text-xs">✕</button>
              </div>
            </div>
          ))}
        </div>
      )}
      {value.length === 0 && !showAdd && <p className="text-xs text-indigo-600">Nenhum {labelItem} selecionado.</p>}
      {showAdd && (
        <div className="p-3 bg-white rounded border border-indigo-200 space-y-2">
          {criandoNovo || disponiveis.length === 0 ? (
            <div>
              <input type="text" value={novoNome} onChange={e => setNovoNome(e.target.value)}
                placeholder={`Nome do novo ${labelItem}`} autoFocus
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
              {disponiveis.length > 0 && (
                <button type="button" onClick={() => { setCriandoNovo(false); setNovoNome('') }} className="text-xs text-indigo-600 hover:underline mt-1">
                  Ou selecione um já cadastrado
                </button>
              )}
            </div>
          ) : (
            <div>
              <select value={selectedId} onChange={e => handleSelect(e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                <option value="">Selecione...</option>
                {disponiveis.map(p => <option key={p.id} value={String(p.id)}>{p.nome} (V:{p.perc_venda}% D:{p.perc_defesa}%)</option>)}
              </select>
              <button type="button" onClick={() => setCriandoNovo(true)} className="text-xs text-indigo-600 hover:underline mt-1">
                + Criar novo {labelItem}
              </button>
            </div>
          )}
          <div className="grid grid-cols-2 gap-2">
            <input type="number" step="0.01" min="0" max="100" value={percVenda} onChange={e => setPercVenda(e.target.value)} placeholder="% Venda" className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm" />
            <input type="number" step="0.01" min="0" max="100" value={percDefesa} onChange={e => setPercDefesa(e.target.value)} placeholder="% Defesa" className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm" />
          </div>
          <div className="flex gap-2 justify-end">
            <button type="button" onClick={fecharAdd} className="px-3 py-1 text-xs text-gray-600 border rounded-lg">Cancelar</button>
            <button type="button" onClick={handleAdd} disabled={salvandoNovo || (criandoNovo || disponiveis.length === 0 ? !novoNome.trim() : !selectedId)} className="px-3 py-1 text-xs text-white bg-indigo-600 rounded-lg disabled:opacity-50">
              {salvandoNovo ? 'Salvando...' : 'Vincular'}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

export default function NovoLeilaoModerno() {
  const { toasts, success, error, hideToast } = useToast()
  const { organizadorAtivo } = useOrganizador()
  const [loading, setLoading] = useState(false)
  const [currentStep, setCurrentStep] = useState(1)
  const [tiposDisponiveis, setTiposDisponiveis] = useState([])
  const [cidades, setCidades] = useState([])
  const [searchCidade, setSearchCidade] = useState('')
  const [showDataFim, setShowDataFim] = useState(false)
  const [organizadorConfig, setOrganizadorConfig] = useState(null)
  const [showNovaComissao, setShowNovaComissao] = useState(false)
  const [showNovoLeiloeiro, setShowNovoLeiloeiro] = useState(false)
  const [doisLeiloeiros, setDoisLeiloeiros] = useState(false)
  const [loadingComissao, setLoadingComissao] = useState(false)
  const [loadingLeiloeiro, setLoadingLeiloeiro] = useState(false)
  const [showEditComissao, setShowEditComissao] = useState(false)
  const [showEditLeiloeiro, setShowEditLeiloeiro] = useState(false)
  const [meiosPagamento, setMeiosPagamento] = useState([])
  const [dadosBancarios, setDadosBancarios] = useState([])
  const [faixasDefesaPadrao, setFaixasDefesaPadrao] = useState([])
  const [usarFaixasCustom, setUsarFaixasCustom] = useState(false)
  const [faixasDefesaCustom, setFaixasDefesaCustom] = useState([])
  const [showNovoMeioPagamento, setShowNovoMeioPagamento] = useState(false)
  
  const [formData, setFormData] = useState({
    codigo_evento: '',
    nome: '',
    data_leilao: '',
    hora_inicio: '',
    data_fim: '',
    hora_fim: '',
    cidade: '',
    uf: '',
    local: '',
    tipo_leilao: 'gado_corte',
    status: 'on',
    link_transmissao: '',
    banner_file: null,
    catalogo_link: '',
    fluxo_comissao: '',
    comissao_padrao_id: '',
    comissao_respeitar_individual: false,
    comissao_venda: '',
    comissao_compra: '',
    comissao_defesa: '',
    leiloeiro_id: '',
    leiloeiro_perc_venda: '',
    leiloeiro_perc_defesa: '',
    leiloeiro2_id: '',
    leiloeiro2_perc_venda: '',
    leiloeiro2_perc_defesa: '',
    valor_gta: 0,
    valor_nota: 0,
    valor_por_animal: 0,
    valor_taxa: 0,
    gta_modo_cobranca: '',
    regra_faturamento: 'por_cabeca',
    meio_pagamento_padrao_id: '',
    dado_bancario_organizador_id: '',
    defesa_modo: 'tabela',
    taxa_inscricao_valor: ''
  })
  
  const steps = [
    { id: 1, name: 'Básico', icon: Settings, description: 'Nome e tipo do leilão' },
    { id: 2, name: 'Local & Data', icon: MapPin, description: 'Onde e quando será' },
    { id: 3, name: 'Mídia', icon: Youtube, description: 'Transmissão e catálogo' },
    { id: 4, name: 'Financeiro', icon: DollarSign, description: 'Comissões e valores' }
  ]

  const organizadorId = organizadorAtivo?.id
  const isOrgFluxoCavalo = organizadorAtivo?.fluxo_leilao === 'comissao_cavalo'
  const tiposParaExibir = isOrgFluxoCavalo
    ? tiposDisponiveis.filter(t => t === 'cavalo')
    : tiposDisponiveis.filter(t => t !== 'cavalo')
  const fluxoComissaoLabel = {
    'primeira_parcela': 'Comissão na Primeira Parcela',
    'gta_nota': 'GTA + Nota',
    'separada': 'Comissão Separada',
    'parcelada': 'Comissão Parcelada',
    'cavalo': '🐴 Cavalo'
  }[organizadorConfig?.fluxo_comissao_padrao] || 'Comissão na Primeira Parcela'

  useEffect(() => {
    if (organizadorId) {
      loadTiposDisponiveis()
      loadOrganizadorConfig()
      // Pré-selecionar cidade do organizador
      if (organizadorAtivo?.cidade) {
        setFormData(prev => ({
          ...prev,
          cidade: organizadorAtivo.cidade,
          uf: organizadorAtivo.estado || organizadorAtivo.uf || '',
        }))
        setSearchCidade(organizadorAtivo.cidade)
      }
    }
  }, [organizadorId])

  useEffect(() => {
    if (isOrgFluxoCavalo && formData.tipo_leilao !== 'cavalo') {
      setFormData(prev => ({ ...prev, tipo_leilao: 'cavalo' }))
    }
  }, [isOrgFluxoCavalo])

  useEffect(() => {
    if (organizadorId && formData.tipo_leilao === 'cavalo') {
      faixasDefesaService.listarPadrao(organizadorId)
        .then(setFaixasDefesaPadrao)
        .catch(err => console.error('Erro ao carregar faixas de defesa padrão:', err))
    }
  }, [organizadorId, formData.tipo_leilao])

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchCidade && searchCidade.length >= 2) {
        searchCidades(searchCidade)
      } else {
        setCidades([])
      }
    }, 300)
    
    return () => clearTimeout(timeoutId)
  }, [searchCidade])

  // Sincronizar searchCidade com formData.cidade
  useEffect(() => {
    if (formData.cidade && searchCidade !== formData.cidade) {
      setSearchCidade(formData.cidade)
    }
  }, [formData.cidade])

  const loadTiposDisponiveis = async () => {
    try {
      const tipos = await leiloesService.getTiposDisponiveis(organizadorId)
      setTiposDisponiveis(tipos.todos || [])
    } catch (err) {
      console.error('Erro ao carregar tipos:', err)
      setTiposDisponiveis(['gado_corte', 'gado_leite', 'gado_elite', 'cavalo', 'beneficente'])
    }
  }

  const loadOrganizadorConfig = async () => {
    try {
      const [comissoesResponse, leiloeirosResponse, meiosPagamentoResponse, dadosBancariosResponse, promotoresResponse] = await Promise.all([
        comissoesService.listar(organizadorId, { ativo: 'true' }),
        leiloeiroService.getByOrganizador(organizadorId),
        meiosPagamentoService.listar(organizadorId),
        organizadoresService.getDadosBancarios(organizadorId).catch(() => []),
        organizadorAtivo?.promotores_habilitado ? import('../../services/promotoresService').then(m => m.default.listar(organizadorId, { ativo: true })) : Promise.resolve([]),
      ])
      
      const comissoes = comissoesResponse?.data || comissoesResponse || []
      const leiloeiros = leiloeirosResponse?.data || leiloeirosResponse || []
      const meiosPag = meiosPagamentoResponse?.data || meiosPagamentoResponse || []
      const dadosBanc = (dadosBancariosResponse?.data || dadosBancariosResponse || []).filter(d => d.is_active)
      const promotores = promotoresResponse || []
      
      setMeiosPagamento(meiosPag)
      setDadosBancarios(dadosBanc)
      
      // Pré-selecionar dado bancário principal
      const dadoPrincipal = dadosBanc.find(d => d.is_principal) || dadosBanc[0]
      const dadoBancarioId = dadoPrincipal ? String(dadoPrincipal.id) : ''
      
      // Buscar fluxo do organizador ativo e mapear para valor do leilão
      const fluxoOrganizador = organizadorAtivo?.fluxo_leilao || 'comissao_primeira_parcela'
      const fluxoMap = {
        'comissao_primeira_parcela': 'primeira_parcela',
        'primeira_parcela': 'primeira_parcela',
        'gta_nota': 'gta_nota',
        'comissao_separada': 'separada',
        'separada': 'separada',
        'comissao_parcelada': 'parcelada',
        'parcelada': 'parcelada',
        'comissao_cavalo': 'cavalo',
        'cavalo': 'cavalo'
      }
      const fluxoComissao = fluxoMap[fluxoOrganizador] || 'primeira_parcela'

      // Pré-preencher valores GTA/Nota do organizador
      if (fluxoComissao === 'gta_nota') {
        try {
          const gtaConfig = await organizadoresService.getConfiguracaoGtaNota(organizadorId)
          setFormData(prev => ({
            ...prev,
            fluxo_comissao: fluxoComissao,
            valor_gta: gtaConfig.gta_valor_por_animal || 0,
            valor_nota: gtaConfig.gta_valor_por_gta_nota || 0,
            valor_por_animal: gtaConfig.gta_valor_por_animal || 0,
            valor_taxa: gtaConfig.gta_valor_taxa || 0,
            gta_modo_cobranca: gtaConfig.gta_modo_cobranca || '',
          }))
        } catch (e) { /* ignora se não tem config */ }
      }
      
      // Encontrar comissão padrão (respeitar a marcada como is_padrao)
      const usaComissaoSocios = organizadorAtivo?.usa_comissao_socios || organizadorAtivo?.produtos_premium?.includes('comissao_socios')
      let comissaoPadrao = ''
      
      if (comissoes?.length > 0) {
        // Priorizar a comissão marcada como padrão (⭐)
        const comissaoMarcadaPadrao = comissoes.find(c => c.is_padrao)
        if (comissaoMarcadaPadrao) {
          comissaoPadrao = String(comissaoMarcadaPadrao.id)
        } else if (usaComissaoSocios) {
          // Fallback: priorizar comissão tipo 'socios'
          const comissaoSocios = comissoes.find(c => c.tipo_comissao === 'socios')
          comissaoPadrao = String(comissaoSocios?.id || comissoes[0]?.id || '')
        } else {
          comissaoPadrao = String(comissoes[0]?.id || '')
        }
      }
      
      // Pré-selecionar leiloeiro padrão
      const leiloeiroPadrao = leiloeiros.find(l => l.is_padrao)
      const leiloeiroId = leiloeiroPadrao ? String(leiloeiroPadrao.id) : ''

      setOrganizadorConfig({
        fluxo_comissao_padrao: fluxoComissao,
        comissoes: comissoes,
        leiloeiros: leiloeiros,
        promotores: promotores,
        usa_comissao_socios: usaComissaoSocios
      })

      // Pré-selecionar promotor padrão
      const promotorPadrao = promotores.find(p => p.is_padrao)
      if (promotorPadrao) {
        setFormData(prev => ({
          ...prev,
          _promotores: [{ promotor_id: promotorPadrao.id, nome: promotorPadrao.nome, perc_venda: promotorPadrao.perc_venda, perc_defesa: promotorPadrao.perc_defesa }],
        }))
      }

      setFormData(prev => ({
        ...prev,
        fluxo_comissao: fluxoComissao,
        comissao_padrao_id: comissaoPadrao,
        leiloeiro_id: leiloeiroId,
        leiloeiro_perc_venda: leiloeiroPadrao?.perc_venda || '',
        leiloeiro_perc_defesa: leiloeiroPadrao?.perc_defesa || '',
        dado_bancario_organizador_id: dadoBancarioId
      }))
    } catch (err) {
      console.error('Erro ao carregar configuração:', err)
      setOrganizadorConfig({
        fluxo_comissao_padrao: 'primeira_parcela',
        comissoes: [],
        leiloeiros: [],
        usa_comissao_socios: false
      })
    }
  }

  const searchCidades = async (termo) => {
    if (termo.length < 2) {
      setCidades([])
      return
    }
    
    try {
      const response = await clientesService.searchCidades(termo)
      setCidades(response.slice(0, 10))
    } catch (err) {
      console.error('Erro ao buscar cidades:', err)
      setCidades([])
    }
  }

  const handleCidadeSelect = (cidade) => {
    setFormData(prev => ({
      ...prev,
      cidade: cidade.nome,
      uf: cidade.uf
    }))
    setSearchCidade(cidade.nome)
    setCidades([])
  }

  const handleBannerUpload = (event) => {
    event.preventDefault()
    event.stopPropagation()
    
    const file = event.target.files[0]
    if (file) {
      // Validar tipo de arquivo
      if (!file.type.startsWith('image/')) {
        error('Por favor, selecione apenas arquivos de imagem')
        return
      }
      
      // Validar tamanho (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        error('Arquivo muito grande. Máximo 5MB')
        return
      }
      
      setFormData({ ...formData, banner_file: file })
    }
    
    // Limpar o input para permitir selecionar o mesmo arquivo novamente
    event.target.value = ''
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (!organizadorId) {
      error('Nenhum organizador selecionado')
      return
    }

    // Validações específicas
    if (!formData.cidade?.trim()) {
      error('Cidade é obrigatória')
      return
    }

    if (!formData.uf?.trim()) {
      error('UF é obrigatória')
      return
    }

    if (!formData.hora_inicio?.trim()) {
      error('Hora de início do leilão é obrigatória')
      return
    }

    // Validações para fluxo GTA + Nota
    // Obs: valor 0 é válido (organizador pode optar por não cobrar), só bloqueia se não houver valor configurado
    if (formData.fluxo_comissao === 'gta_nota' && formData.tipo_leilao !== 'beneficente') {
      if (formData.valor_por_animal === '' || formData.valor_por_animal === null || formData.valor_por_animal === undefined || isNaN(parseFloat(formData.valor_por_animal))) {
        error('Valor por Animal não configurado. Configure em Cadastros > GTA e Nota.')
        return
      }

      if (formData.valor_nota === '' || formData.valor_nota === null || formData.valor_nota === undefined || isNaN(parseFloat(formData.valor_nota))) {
        error('Valor NOTA não configurado. Configure em Cadastros > GTA e Nota.')
        return
      }
    }

    // Validações para regra de faturamento por parcela
    if (formData.regra_faturamento === 'por_parcela') {
      if (!formData.meio_pagamento_padrao_id) {
        error('Selecione um meio de pagamento para faturamento por parcela')
        return
      }
    }

    try {
      setLoading(true)
      
      const leilaoData = {
        codigo_evento: formData.codigo_evento,
        nome: formData.nome,
        data_leilao: formData.data_leilao,
        hora_inicio: formData.hora_inicio,
        data_fim: formData.data_fim || null,
        hora_fim: formData.hora_fim || null,
        cidade: formData.cidade || null,
        uf: formData.uf || null,
        local: formData.local || null,
        tipo_leilao: formData.tipo_leilao,
        status: formData.status,
        link_transmissao: formData.link_transmissao || null,
        catalogo_link: formData.catalogo_link || null,
        fluxo_comissao: formData.fluxo_comissao || null,
        comissao_padrao_id: parseInt(formData.comissao_padrao_id) || null,
        comissao_respeitar_individual: formData.tipo_leilao === 'beneficente' ? !!formData.comissao_respeitar_individual : false,
        leiloeiro_id: parseInt(formData.leiloeiro_id) || null,
        leiloeiro_perc_venda: parseFloat(formData.leiloeiro_perc_venda) || null,
        leiloeiro_perc_defesa: parseFloat(formData.leiloeiro_perc_defesa) || null,
        valor_gta: parseFloat(formData.valor_por_animal) || 0,
        valor_nota: parseFloat(formData.valor_nota) || 0,
        valor_por_animal: parseFloat(formData.valor_por_animal) || 0,
        valor_taxa: parseFloat(formData.valor_taxa) || 0,
        gta_modo_cobranca: formData.gta_modo_cobranca || null,
        regra_faturamento: formData.regra_faturamento || 'por_cabeca',
        meio_pagamento_padrao_id: formData.regra_faturamento === 'por_parcela' ? parseInt(formData.meio_pagamento_padrao_id) || null : null,
        dado_bancario_organizador_id: parseInt(formData.dado_bancario_organizador_id) || null,
        ...(formData.tipo_leilao === 'cavalo' ? {
          defesa_modo: formData.defesa_modo || 'tabela',
          taxa_inscricao_valor: formData.taxa_inscricao_valor === '' ? null : parseFloat(formData.taxa_inscricao_valor)
        } : {})
      }
      
      // Remover banner_file dos dados (será enviado separadamente)
      delete leilaoData.banner_file
      
      const leilaoCriado = await leiloesService.create(organizadorId, leilaoData, formData.banner_file)

      // Sincronizar leiloeiros em leilao_leiloeiros
      const leiloeirosSalvar = []
      if (leilaoData.leiloeiro_id) leiloeirosSalvar.push({
        leiloeiro_id: leilaoData.leiloeiro_id,
        perc_venda:   leilaoData.leiloeiro_perc_venda  || 0,
        perc_defesa:  leilaoData.leiloeiro_perc_defesa || 0,
      })
      if (formData.leiloeiro2_id) leiloeirosSalvar.push({
        leiloeiro_id: parseInt(formData.leiloeiro2_id),
        perc_venda:   parseFloat(formData.leiloeiro2_perc_venda)  || 0,
        perc_defesa:  parseFloat(formData.leiloeiro2_perc_defesa) || 0,
      })
      for (const ll of leiloeirosSalvar) {
        try {
          await leiloeiroService.adicionarAoLeilao(organizadorId, leilaoCriado.id, ll)
        } catch (e) {
          if (e.response?.status !== 409) console.error('Erro ao vincular leiloeiro ao leilão:', e)
        }
      }

      // Sincronizar promotores
      if (formData._promotores && formData._promotores.length > 0) {
        const promotoresService = (await import('../../services/promotoresService')).default
        for (const p of formData._promotores) {
          try {
            await promotoresService.vincular(organizadorId, leilaoCriado.id, {
              promotor_id: p.promotor_id,
              perc_venda: p.perc_venda || 0,
              perc_defesa: p.perc_defesa || 0,
            })
          } catch (e) {
            if (e.response?.status !== 409) console.error('Erro ao vincular promotor ao leilão:', e)
          }
        }
      }

      // Salvar faixas de defesa customizadas (fluxo cavalo), se o usuário optou por personalizar
      if (formData.tipo_leilao === 'cavalo' && usarFaixasCustom && faixasDefesaCustom.length > 0) {
        try {
          const payload = faixasDefesaCustom.map((f, i) => ({
            valor_min: parseFloat(f.valor_min) || 0,
            valor_max: f.valor_max === '' || f.valor_max === null ? null : parseFloat(f.valor_max),
            valor_fixo: parseFloat(f.valor_fixo) || 0,
            ordem: i,
          }))
          await faixasDefesaService.salvarPorLeilao(organizadorId, leilaoCriado.id, payload)
        } catch (e) {
          console.error('Erro ao salvar faixas de defesa customizadas:', e)
        }
      }

      success('Leilão criado com sucesso!')

      setTimeout(() => {
        window.location.href = '/leiloes'
      }, 1500)
      
    } catch (err) {
      console.error('Erro ao criar leilão:', err)
      
      // Tratar erro 422 (validação)
      if (err.response?.status === 422) {
        const validationErrors = err.response?.data?.detail
        if (Array.isArray(validationErrors)) {
          const errorMessages = validationErrors.map(e => `${e.loc?.join('.')}: ${e.msg}`).join(', ')
          error(`Erro de validação: ${errorMessages}`)
        } else {
          error('Erro de validação nos dados enviados')
        }
      } else {
        const errorMessage = err.response?.data?.detail || err.message || 'Erro ao criar leilão'
        error(errorMessage)
      }
    } finally {
      setLoading(false)
    }
  }

  const getTipoLabel = (tipo) => {
    const tipos = {
      'gado_corte': 'Gado de Corte',
      'gado_leite': 'Gado de Leite',
      'gado_elite': 'Gado de Elite',
      'cavalo': 'Cavalo',
      'beneficente': 'Beneficente'
    }
    return tipos[tipo] || tipo
  }

  const isPremium = (tipo) => {
    return ['gado_leite', 'gado_elite'].includes(tipo)
  }

  const nextStep = () => {
    if (currentStep < 4) setCurrentStep(currentStep + 1)
  }

  // Avançar com Enter (exceto em textareas)
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && e.target.tagName !== 'TEXTAREA' && e.target.type !== 'submit') {
      e.preventDefault()
      if (currentStep < 4) {
        nextStep()
      }
    }
  }

  const prevStep = () => {
    if (currentStep > 1) setCurrentStep(currentStep - 1)
  }

  const showGtaNota = formData.fluxo_comissao === 'gta_nota'

  const handleSaveComissao = async (comissaoData) => {
    try {
      setLoadingComissao(true)
      const response = await comissoesService.criar(organizadorId, comissaoData)
      const novaComissao = response.data || response
      
      // Atualizar lista de comissões
      setOrganizadorConfig(prev => ({
        ...prev,
        comissoes: [...(prev?.comissoes || []), novaComissao]
      }))
      
      // Selecionar a nova comissão
      setFormData(prev => ({ ...prev, comissao_padrao_id: novaComissao.id }))
      
      setShowNovaComissao(false)
      success('Comissão criada com sucesso!')
    } catch (err) {
      console.error('Erro ao criar comissão:', err)
      error('Erro ao criar comissão')
    } finally {
      setLoadingComissao(false)
    }
  }

  const handleSaveMeioPagamento = async (meioPagamentoData) => {
    const novoMeio = await meiosPagamentoService.criar(organizadorId, meioPagamentoData)
    const meio = novoMeio.data || novoMeio

    setMeiosPagamento(prev => [...prev, meio])
    setFormData(prev => ({ ...prev, meio_pagamento_padrao_id: meio.id }))
    setShowNovoMeioPagamento(false)
    success('Meio de pagamento criado com sucesso!')
  }

  const handleSaveLeiloeiro = async (leiloeiroData) => {
    try {
      setLoadingLeiloeiro(true)
      const novoLeiloeiro = await leiloeiroService.create(organizadorId, leiloeiroData)
      
      // Atualizar lista de leiloeiros
      setOrganizadorConfig(prev => ({
        ...prev,
        leiloeiros: [...(prev?.leiloeiros || []), novoLeiloeiro]
      }))
      
      // Selecionar o novo leiloeiro
      setFormData(prev => ({
        ...prev,
        leiloeiro_id: novoLeiloeiro.id,
        leiloeiro_perc_venda: novoLeiloeiro.perc_venda,
        leiloeiro_perc_defesa: novoLeiloeiro.perc_defesa
      }))
      
      setShowNovoLeiloeiro(false)
      success('Leiloeiro criado com sucesso!')
    } catch (err) {
      console.error('Erro ao criar leiloeiro:', err)
      error('Erro ao criar leiloeiro')
    } finally {
      setLoadingLeiloeiro(false)
    }
  }

  const handleEditComissao = async (comissaoData) => {
    try {
      setLoadingComissao(true)
      const response = await comissoesService.atualizar(organizadorId, formData.comissao_padrao_id, comissaoData)
      const comissaoAtualizada = response.data || response
      
      // Atualizar lista de comissões
      setOrganizadorConfig(prev => ({
        ...prev,
        comissoes: prev.comissoes.map(c => c.id === comissaoAtualizada.id ? comissaoAtualizada : c)
      }))
      
      setShowEditComissao(false)
      success('Comissão atualizada com sucesso!')
    } catch (err) {
      console.error('Erro ao atualizar comissão:', err)
      if (err.response?.status === 409) {
        error('Não é possível editar esta comissão pois ela já foi utilizada em lotes faturados. Alterar os valores afetaria o faturamento e as parcelas.')
      } else {
        error('Erro ao atualizar comissão')
      }
    } finally {
      setLoadingComissao(false)
    }
  }

  const handleEditLeiloeiro = async (leiloeiroData) => {
    try {
      setLoadingLeiloeiro(true)
      const leiloeiroAtualizado = await leiloeiroService.update(organizadorId, formData.leiloeiro_id, leiloeiroData)
      
      // Atualizar lista de leiloeiros
      setOrganizadorConfig(prev => ({
        ...prev,
        leiloeiros: prev.leiloeiros.map(l => l.id === leiloeiroAtualizado.id ? leiloeiroAtualizado : l)
      }))
      
      // Atualizar formData
      setFormData(prev => ({
        ...prev,
        leiloeiro_perc_venda: leiloeiroAtualizado.perc_venda,
        leiloeiro_perc_defesa: leiloeiroAtualizado.perc_defesa
      }))
      
      setShowEditLeiloeiro(false)
      success('Leiloeiro atualizado com sucesso!')
    } catch (err) {
      console.error('Erro ao atualizar leiloeiro:', err)
      error('Erro ao atualizar leiloeiro')
    } finally {
      setLoadingLeiloeiro(false)
    }
  }

  return (
    <>
      {/* Toasts */}
      {toasts.map(toast => (
        <Toast
          key={toast.id}
          message={toast.message}
          type={toast.type}
          duration={toast.duration}
          onClose={() => hideToast(toast.id)}
        />
      ))}

      <div className="page-content" onKeyDown={handleKeyDown}>
        {/* Header */}
        <div className="flex items-center space-x-4">
          <button
            onClick={() => window.location.href = '/leiloes'}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Criar Novo Leilão</h1>
            <p className="text-sm text-gray-500">
              Preencha as informações para criar seu leilão
            </p>
          </div>
        </div>

        {/* Progress Steps */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 px-4 py-4">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center justify-between">
              {steps.map((step, index) => {
                const Icon = step.icon
                const isActive = currentStep === step.id
                const isCompleted = currentStep > step.id

                return (
                  <div key={step.id} className="flex items-center flex-shrink-0">
                    <button
                      type="button"
                      onClick={() => setCurrentStep(step.id)}
                      className={`flex items-center space-x-1 sm:space-x-3 p-1 sm:p-2 rounded-lg transition-colors hover:bg-gray-50 ${
                        isActive ? 'text-rematei-blue' : isCompleted ? 'text-green-600' : 'text-gray-400'
                      }`}
                    >
                      <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center border-2 flex-shrink-0 ${
                        isActive ? 'border-rematei-blue bg-rematei-blue text-white' :
                        isCompleted ? 'border-green-600 bg-green-600 text-white' :
                        'border-gray-300 bg-white'
                      }`}>
                        {isCompleted ? <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5" /> : <Icon className="w-4 h-4 sm:w-5 sm:h-5" />}
                      </div>
                      <div className="hidden md:block">
                        <p className="font-medium">{step.name}</p>
                        <p className="text-xs text-gray-500">{step.description}</p>
                      </div>
                    </button>
                    {index < steps.length - 1 && (
                      <div className={`w-3 sm:w-16 h-0.5 mx-1 sm:mx-4 flex-shrink-0 ${
                        isCompleted ? 'bg-green-600' : 'bg-gray-300'
                      }`} />
                    )}
                  </div>
                )
              })}
            </div>
          </div>
        </div>

        {/* Input file fora do form para evitar submit */}
        <input
          type="file"
          accept="image/*"
          onChange={handleBannerUpload}
          className="hidden"
          id="banner-upload"
        />
        
        {/* Form Content */}
        <div>
            {/* Step 1: Básico */}
            {currentStep === 1 && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Nome do Leilão *
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.nome}
                      onChange={(e) => setFormData({...formData, nome: e.target.value})}
                      className="input-rematei text-lg"
                      placeholder={isOrgFluxoCavalo ? "Ex: Leilão de Cavalos - Haras Boa Vista" : "Ex: Leilão de Gado de Corte - Fazenda São João"}
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Tipo de Leilão *
                      </label>
                      <select
                        required
                        value={formData.tipo_leilao}
                        onChange={(e) => setFormData({...formData, tipo_leilao: e.target.value})}
                        className="input-rematei"
                      >
                        {tiposParaExibir.map(tipo => (
                          <option key={tipo} value={tipo}>
                            {getTipoLabel(tipo)} {isPremium(tipo) ? '(Premium)' : ''}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Status Inicial
                      </label>
                      <select
                        value={formData.status}
                        onChange={(e) => setFormData({...formData, status: e.target.value})}
                        className="input-rematei"
                      >
                        <option value="on">Ativo</option>
                        <option value="off">Inativo</option>
                      </select>
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Código do Evento
                    </label>
                    <input
                      type="text"
                      value={formData.codigo_evento}
                      onChange={(e) => setFormData({...formData, codigo_evento: e.target.value.toUpperCase()})}
                      className="input-rematei"
                      placeholder="Ex: LEI2024001 (opcional)"
                      maxLength="50"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Código único para identificar o evento (opcional)
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Step 2: Local & Data */}
            {currentStep === 2 && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Data do Leilão *
                      </label>
                      <input
                        type="date"
                        required
                        value={formData.data_leilao}
                        onChange={(e) => setFormData({...formData, data_leilao: e.target.value})}
                        className="input-rematei"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Hora de Início *
                      </label>
                      <input
                        type="time"
                        required
                        value={formData.hora_inicio}
                        onChange={(e) => setFormData({...formData, hora_inicio: e.target.value})}
                        className="input-rematei"
                      />
                    </div>
                  </div>

                  <div className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      id="showDataFim"
                      checked={showDataFim}
                      onChange={(e) => setShowDataFim(e.target.checked)}
                      className="h-4 w-4 text-rematei-blue focus:ring-rematei-blue border-gray-300 rounded"
                    />
                    <label htmlFor="showDataFim" className="text-sm font-medium text-gray-700">
                      Definir data e hora de fim
                    </label>
                  </div>

                  {showDataFim && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-4 bg-blue-50 rounded-lg">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Data de Fim
                        </label>
                        <input
                          type="date"
                          min={formData.data_leilao}
                          value={formData.data_fim}
                          onChange={(e) => setFormData({...formData, data_fim: e.target.value})}
                          className="input-rematei"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Hora de Fim
                        </label>
                        <input
                          type="time"
                          value={formData.hora_fim}
                          onChange={(e) => setFormData({...formData, hora_fim: e.target.value})}
                          className="input-rematei"
                        />
                      </div>
                    </div>
                  )}

                  <div className="relative">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Cidade *
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        required
                        value={searchCidade}
                        onChange={(e) => {
                          setSearchCidade(e.target.value)
                          setFormData(prev => ({ ...prev, cidade: e.target.value, uf: '' }))
                        }}
                        className="input-rematei pr-10"
                        placeholder="Digite para buscar a cidade..."
                      />
                      <Search className="absolute right-3 top-3 w-4 h-4 text-gray-400" />
                    </div>
                    
                    {cidades.length > 0 && (
                      <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                        {cidades.map((cidade, index) => (
                          <button
                            key={index}
                            type="button"
                            onClick={() => handleCidadeSelect(cidade)}
                            className="w-full px-4 py-2 text-left hover:bg-gray-50 flex justify-between items-center"
                          >
                            <span>{cidade.nome}</span>
                            <span className="text-sm text-gray-500">{cidade.uf}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        UF *
                      </label>
                      <input
                        type="text"
                        required
                        maxLength="2"
                        value={formData.uf}
                        onChange={(e) => setFormData({...formData, uf: e.target.value.toUpperCase()})}
                        className="input-rematei"
                        placeholder="Ex: MG"
                        readOnly={formData.cidade}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Local
                      </label>
                      <input
                        type="text"
                        value={formData.local}
                        onChange={(e) => setFormData({...formData, local: e.target.value})}
                        className="input-rematei"
                        placeholder="Ex: Centro de Eventos"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Step 3: Mídia */}
            {currentStep === 3 && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Link da Transmissão (YouTube)
                    </label>
                    <input
                      type="url"
                      value={formData.link_transmissao}
                      onChange={(e) => setFormData({...formData, link_transmissao: e.target.value})}
                      className="input-rematei"
                      placeholder="https://youtube.com/watch?v=..."
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Banner do Leilão
                    </label>
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-gray-400 transition-colors">
                      <Upload className="w-8 h-8 mx-auto text-gray-400 mb-2" />
                      <p className="text-sm text-gray-600 mb-2">
                        Clique para fazer upload ou arraste a imagem aqui
                      </p>

                      <label
                        htmlFor="banner-upload"
                        className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 cursor-pointer"
                      >
                        Selecionar Arquivo
                      </label>
                      {formData.banner_file && (
                        <div className="mt-3 flex items-center justify-center space-x-2">
                          <p className="text-sm text-green-600">
                            ✓ {formData.banner_file.name}
                          </p>
                          <div
                            onClick={(e) => {
                              e.preventDefault()
                              e.stopPropagation()
                              setFormData({ ...formData, banner_file: null })
                            }}
                            className="text-red-500 hover:text-red-700 cursor-pointer"
                          >
                            <X className="w-4 h-4" />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                </div>
              </div>
            )}

            {/* Step 4: Financeiro */}
            {currentStep === 4 && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
                <div className="space-y-6">
                  {formData.tipo_leilao === 'beneficente' ? (
                    <div className="text-center py-8">
                      <div className="p-6 bg-green-50 rounded-lg">
                        <h3 className="text-lg font-medium text-green-800 mb-2">Leilão Beneficente</h3>
                        <p className="text-green-600">
                          Para leilões beneficentes, as configurações financeiras são opcionais.
                          Você pode pular esta etapa ou configurar se necessário.
                        </p>
                      </div>
                      <div className="mt-4 text-left">
                        <ComissaoBeneficenteOpcao
                          respeitarIndividual={formData.comissao_respeitar_individual}
                          onChange={(v) => setFormData(prev => ({ ...prev, comissao_respeitar_individual: v }))}
                        />
                      </div>
                    </div>
                  ) : (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Fluxo de Comissão
                      </label>
                      <div className="p-4 bg-blue-50 rounded-lg mb-4">
                        <p className="text-sm text-blue-800">
                          <strong>Configuração do Organizador:</strong> {fluxoComissaoLabel}
                        </p>
                      </div>
                      <div className="input-rematei bg-gray-50 text-gray-700">
                        {fluxoComissaoLabel}
                      </div>
                      <input type="hidden" name="fluxo_comissao" value={formData.fluxo_comissao} />
                    </div>
                  )}

                  {formData.tipo_leilao !== 'beneficente' && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <label className="block text-sm font-medium text-gray-700">
                            Comissão Padrão
                          </label>
                          <button
                            type="button"
                            onClick={() => setShowNovaComissao(true)}
                            className="text-xs text-rematei-blue hover:text-rematei-blue-dark"
                          >
                            + Nova Comissão
                          </button>
                        </div>
                        <select
                          value={formData.comissao_padrao_id}
                          onChange={(e) => {
                            const comissao = organizadorConfig?.comissoes?.find(c => c.id === parseInt(e.target.value))
                            setFormData({
                              ...formData,
                              comissao_padrao_id: e.target.value,
                              comissao_venda: comissao?.comissao_venda || '',
                              comissao_compra: comissao?.comissao_compra || '',
                              comissao_defesa: comissao?.comissao_defesa || ''
                            })
                          }}
                          className="input-rematei"
                        >
                          <option value="">Selecione uma comissão</option>
                          {organizadorConfig?.comissoes?.map(comissao => (
                            <option key={comissao.id} value={comissao.id}>
                              {comissao.nome} {comissao.tipo_comissao === 'socios' ? '(Sócios)' : `(V:${comissao.comissao_venda}% C:${comissao.comissao_compra}% D:${comissao.comissao_defesa}%)`}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <label className="block text-sm font-medium text-gray-700">Leiloeiro</label>
                          <div className="flex items-center gap-3">
                            {(organizadorConfig?.leiloeiros || []).length > 1 && formData.leiloeiro_id && (
                              <label className="flex items-center gap-1.5 text-xs text-gray-500 cursor-pointer select-none">
                                <input
                                  type="checkbox"
                                  checked={doisLeiloeiros}
                                  onChange={e => {
                                    setDoisLeiloeiros(e.target.checked)
                                    if (!e.target.checked) setFormData(prev => ({ ...prev, leiloeiro2_id: '', leiloeiro2_perc_venda: '', leiloeiro2_perc_defesa: '' }))
                                  }}
                                  className="rounded"
                                />
                                2 leiloeiros
                              </label>
                            )}
                            <button type="button" onClick={() => setShowNovoLeiloeiro(true)} className="text-xs text-rematei-blue hover:text-rematei-blue-dark">
                              + Novo Leiloeiro
                            </button>
                          </div>
                        </div>

                        <div className={doisLeiloeiros ? 'grid grid-cols-2 gap-2' : ''}>
                          <select
                            value={formData.leiloeiro_id}
                            onChange={(e) => {
                              const leiloeiro = organizadorConfig?.leiloeiros?.find(l => l.id === parseInt(e.target.value))
                              setFormData(prev => ({
                                ...prev,
                                leiloeiro_id: e.target.value,
                                leiloeiro_perc_venda:  leiloeiro?.perc_venda  || '',
                                leiloeiro_perc_defesa: leiloeiro?.perc_defesa || '',
                                leiloeiro2_id: '', leiloeiro2_perc_venda: '', leiloeiro2_perc_defesa: '',
                              }))
                              setDoisLeiloeiros(false)
                            }}
                            className="input-rematei"
                          >
                            <option value="">Selecione um leiloeiro</option>
                            {organizadorConfig?.leiloeiros?.map(l => (
                              <option key={l.id} value={l.id}>{l.nome}</option>
                            ))}
                          </select>

                          {doisLeiloeiros && (
                            <select
                              value={formData.leiloeiro2_id}
                              onChange={(e) => {
                                const l2 = organizadorConfig?.leiloeiros?.find(l => l.id === parseInt(e.target.value))
                                setFormData(prev => ({
                                  ...prev,
                                  leiloeiro2_id:          e.target.value,
                                  leiloeiro2_perc_venda:  l2?.perc_venda  || '',
                                  leiloeiro2_perc_defesa: l2?.perc_defesa || '',
                                }))
                              }}
                              className="input-rematei"
                            >
                              <option value="">2º leiloeiro</option>
                              {(organizadorConfig?.leiloeiros || [])
                                .filter(l => String(l.id) !== String(formData.leiloeiro_id))
                                .map(l => <option key={l.id} value={l.id}>{l.nome}</option>)
                              }
                            </select>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {formData.comissao_padrao_id && (
                    <div className="p-4 bg-blue-50 rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <div>
                          <h4 className="font-medium text-gray-700">Comissão Selecionada</h4>
                          {(() => {
                            const comissao = organizadorConfig?.comissoes?.find(c => c.id === parseInt(formData.comissao_padrao_id))
                            if (!comissao) return null
                            
                            if (comissao.tipo_comissao === 'socios') {
                              return (
                                <div className="text-sm text-gray-600 space-y-1">
                                  <div className="font-medium text-purple-700">Comissão por Sócios:</div>
                                  <div>👥 Ambos: C:{comissao.ambos_socios_comprador || 0}% V:{comissao.ambos_socios_vendedor || 0}% D:{comissao.ambos_socios_defesa || 0}%</div>
                                  <div>👤 Só Comp: C:{comissao.comprador_socio_comprador || 0}% V:{comissao.comprador_socio_vendedor || 0}% D:{comissao.comprador_socio_defesa || 0}%</div>
                                  <div>👤 Só Vend: C:{comissao.vendedor_socio_comprador || 0}% V:{comissao.vendedor_socio_vendedor || 0}% D:{comissao.vendedor_socio_defesa || 0}%</div>
                                  <div>❌ Nenhum: C:{comissao.comissao_compra || 0}% V:{comissao.comissao_venda || 0}% D:{comissao.comissao_defesa || 0}%</div>
                                </div>
                              )
                            } else {
                              return (
                                <p className="text-sm text-gray-600">
                                  V:{comissao.comissao_venda || '0'}% | C:{comissao.comissao_compra || '0'}% | D:{comissao.comissao_defesa || '0'}%
                                </p>
                              )
                            }
                          })()} 
                        </div>
                        <button
                          type="button"
                          onClick={async () => {
                            try {
                              const response = await comissoesService.verificarEmUso(organizadorId, formData.comissao_padrao_id)
                              const { em_uso, qtd_lotes } = response.data || response
                              if (em_uso) {
                                error(`Não é possível editar esta comissão pois ela já foi utilizada em ${qtd_lotes} lote(s) faturado(s). Alterar os valores afetaria o faturamento e as parcelas.`)
                                return
                              }
                            } catch (err) {
                              console.error('Erro ao verificar uso da comissão:', err)
                            }
                            setShowEditComissao(true)
                          }}
                          className="text-xs text-rematei-blue hover:text-rematei-blue-dark"
                        >
                          Editar
                        </button>
                      </div>
                    </div>
                  )}

                  {formData.leiloeiro_id && (
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="font-medium text-gray-700">Leiloeiro Selecionado</h4>
                          <p className="text-sm text-gray-600">
                            Venda:{formData.leiloeiro_perc_venda || '0'}% | Defesa:{formData.leiloeiro_perc_defesa || '0'}%
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={() => setShowEditLeiloeiro(true)}
                          className="text-xs text-rematei-blue hover:text-rematei-blue-dark"
                        >
                          Editar
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Regra de Faturamento */}
                  {formData.tipo_leilao !== 'beneficente' && (
                    <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                      <h4 className="font-medium text-gray-800 mb-3">Regra de Faturamento</h4>
                      <div className="flex space-x-3 mb-4">
                        <button
                          type="button"
                          onClick={() => setFormData(prev => ({ ...prev, regra_faturamento: 'por_cabeca', meio_pagamento_padrao_id: '' }))}
                          className={`flex-1 px-4 py-3 rounded-lg text-sm font-medium border-2 transition-colors ${
                            formData.regra_faturamento === 'por_cabeca'
                              ? 'border-green-600 bg-green-600 text-white'
                              : 'border-gray-200 bg-white text-gray-700 hover:border-green-300'
                          }`}
                        >
                          Faturamento por Cabeça
                        </button>
                        <button
                          type="button"
                          onClick={() => setFormData(prev => ({ ...prev, regra_faturamento: 'por_parcela' }))}
                          className={`flex-1 px-4 py-3 rounded-lg text-sm font-medium border-2 transition-colors ${
                            formData.regra_faturamento === 'por_parcela'
                              ? 'border-green-600 bg-green-600 text-white'
                              : 'border-gray-200 bg-white text-gray-700 hover:border-green-300'
                          }`}
                        >
                          Faturamento por Parcela
                        </button>
                      </div>

                      {formData.regra_faturamento === 'por_cabeca' && (
                        <p className="text-sm text-gray-600">
                          O valor de venda será calculado por cabeça × quantidade de animais (padrão).
                        </p>
                      )}

                      {formData.regra_faturamento === 'por_parcela' && (
                        <div className="space-y-4">
                          <p className="text-sm text-gray-600">
                            O valor de venda será um valor fixo por parcela. É obrigatório selecionar um meio de pagamento.
                          </p>

                          <div>
                            <div className="flex items-center justify-between mb-1">
                              <label className="block text-sm font-medium text-gray-700">
                                Meio de Pagamento *
                              </label>
                              <button
                                type="button"
                                onClick={() => setShowNovoMeioPagamento(true)}
                                className="text-xs text-rematei-blue hover:text-rematei-blue-dark"
                              >
                                + Novo Meio de Pagamento
                              </button>
                            </div>
                            <select
                              value={formData.meio_pagamento_padrao_id}
                              onChange={(e) => setFormData(prev => ({ ...prev, meio_pagamento_padrao_id: e.target.value }))}
                              className="input-rematei"
                              required
                            >
                              <option value="">Selecione um meio de pagamento</option>
                              {meiosPagamento.map(meio => (
                                <option key={meio.id} value={meio.id}>
                                  {meio.nome} ({meio.tipo === 'A_VISTA' ? 'À Vista' : meio.tipo === 'PRAZO' ? 'A Prazo' : 'Parcelado'})
                                </option>
                              ))}
                            </select>
                            {meiosPagamento.length === 0 && (
                              <p className="text-xs text-amber-600 mt-1">
                                Nenhum meio de pagamento cadastrado.{' '}
                                <button
                                  type="button"
                                  onClick={() => setShowNovoMeioPagamento(true)}
                                  className="underline font-medium"
                                >
                                  Cadastrar agora
                                </button>
                              </p>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Dado Bancário do Organizador para este Leilão */}
                  {formData.tipo_leilao !== 'beneficente' && dadosBancarios.length > 0 && (
                    <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                      <h4 className="font-medium text-gray-800 mb-2 flex items-center gap-2">
                        <DollarSign size={16} className="text-purple-600" />
                        Dado Bancário para Comissão
                      </h4>
                      <p className="text-xs text-gray-500 mb-3">
                        Conta bancária que aparecerá nos relatórios deste leilão para recebimento de comissões.
                      </p>
                      <select
                        value={formData.dado_bancario_organizador_id}
                        onChange={(e) => setFormData(prev => ({ ...prev, dado_bancario_organizador_id: e.target.value }))}
                        className="input-rematei"
                      >
                        <option value="">Selecione uma conta bancária</option>
                        {dadosBancarios.map(db => (
                          <option key={db.id} value={db.id}>
                            {db.banco} - AG {db.agencia} - CC {db.conta}
                            {db.pix ? ` (PIX: ${db.pix})` : ''}
                            {db.is_principal ? ' ⭐ Principal' : ''}
                          </option>
                        ))}
                      </select>
                      {dadosBancarios.length === 0 && (
                        <p className="text-xs text-amber-600 mt-2">
                          Nenhum dado bancário cadastrado.{' '}
                          <a href="/cadastros/organizador" className="underline font-medium">
                            Cadastrar agora
                          </a>
                        </p>
                      )}
                    </div>
                  )}

                  {showGtaNota && formData.tipo_leilao !== 'beneficente' && (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 p-4 bg-amber-50 rounded-lg">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Valor por Animal (R$)
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={formData.valor_por_animal}
                          disabled
                          className="input-rematei bg-gray-100 cursor-not-allowed"
                          placeholder="0.00"
                        />
                        <p className="text-xs text-gray-500 mt-1">Configurado em Cadastros &gt; GTA e Nota</p>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Valor por GTA/Nota (R$)
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={formData.valor_nota}
                          disabled
                          className="input-rematei bg-gray-100 cursor-not-allowed"
                          placeholder="0.00"
                        />
                        <p className="text-xs text-gray-500 mt-1">Configurado em Cadastros &gt; GTA e Nota</p>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Modo de Cobrança
                        </label>
                        <select
                          value={formData.gta_modo_cobranca || ''}
                          disabled
                          className="input-rematei bg-gray-100 cursor-not-allowed"
                        >
                          <option value="">Usar padrão do organizador</option>
                          <option value="por_vendedor">Por Vendedor</option>
                          <option value="por_lote">Por Lote</option>
                        </select>
                        <p className="text-xs text-gray-500 mt-1">Configurado em Cadastros &gt; GTA e Nota</p>
                      </div>
                    </div>
                  )}

                  {formData.tipo_leilao === 'cavalo' && (
                    <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                      <h4 className="font-medium text-gray-800 mb-2">🐴 Configuração Defesa e Taxa de Inscrição</h4>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Modo de Defesa
                          </label>
                          <div className="flex flex-col sm:flex-row gap-1.5 sm:gap-3">
                            <label className="flex items-center text-sm whitespace-nowrap">
                              <input
                                type="radio"
                                value="tabela"
                                checked={formData.defesa_modo === 'tabela'}
                                onChange={(e) => setFormData({...formData, defesa_modo: e.target.value})}
                                className="mr-1.5"
                              />
                              Tabela por Faixa
                              <span
                                className="ml-1 text-gray-400 cursor-help"
                                title="A defesa vira um valor fixo em R$, definido conforme em qual faixa de valor de venda o lote se encaixa (configurado logo abaixo, em 'Defesa por Faixa de Valor'). Não usa percentual."
                              >
                                ⓘ
                              </span>
                            </label>
                            <label className="flex items-center text-sm whitespace-nowrap">
                              <input
                                type="radio"
                                value="percentual"
                                checked={formData.defesa_modo === 'percentual'}
                                onChange={(e) => setFormData({...formData, defesa_modo: e.target.value})}
                                className="mr-1.5"
                              />
                              Percentual
                            </label>
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Taxa de Inscrição (R$)
                          </label>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={formData.taxa_inscricao_valor}
                            onChange={(e) => setFormData({...formData, taxa_inscricao_valor: e.target.value})}
                            className="input-rematei"
                            placeholder="Opcional"
                          />
                          <p className="text-xs text-gray-500 mt-1">
                            Cobrada do vendedor por lote. Deixe em branco para não cobrar.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {formData.tipo_leilao === 'cavalo' && formData.defesa_modo !== 'percentual' && (
                    <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-medium text-gray-800">🐴 Defesa por Faixa de Valor</h4>
                        {(faixasDefesaPadrao.length > 0 || usarFaixasCustom) && (
                          <button
                            type="button"
                            onClick={() => {
                              if (!usarFaixasCustom) {
                                setFaixasDefesaCustom(faixasDefesaPadrao.map(f => ({ valor_min: f.valor_min, valor_max: f.valor_max, valor_fixo: f.valor_fixo })))
                              }
                              setUsarFaixasCustom(prev => !prev)
                            }}
                            className="text-xs text-rematei-blue hover:text-rematei-blue-dark"
                          >
                            {usarFaixasCustom ? 'Usar padrão do organizador' : 'Personalizar para este leilão'}
                          </button>
                        )}
                      </div>
                      <p className="text-sm text-gray-600 mb-3">
                        Neste leilão a defesa é um valor fixo conforme a faixa de valor da venda (não percentual).
                      </p>

                      {usarFaixasCustom ? (
                        <FaixasDefesaEditor faixas={faixasDefesaCustom} onChange={setFaixasDefesaCustom} />
                      ) : faixasDefesaPadrao.length > 0 ? (
                        <ul className="text-sm text-gray-700 space-y-1">
                          {faixasDefesaPadrao.map(f => (
                            <li key={f.id}>
                              {f.valor_min > 0 ? `De R$ ${Number(f.valor_min).toFixed(2)} ` : 'Até '}
                              {f.valor_max != null ? `até R$ ${Number(f.valor_max).toFixed(2)}` : 'em diante'}
                              {' → '}<strong>R$ {Number(f.valor_fixo).toFixed(2)}</strong>
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p className="text-sm text-amber-700">
                          Nenhuma faixa padrão cadastrada. Configure em Comissões &gt; Defesa por Faixa de Valor, ou clique em "Personalizar para este leilão" acima.
                        </p>
                      )}
                    </div>
                  )}
                </div>

                {/* Promotores do Leilão na criação */}
                {organizadorAtivo?.promotores_habilitado && (
                  <PromotoresLeilaoNovo
                    organizadorId={organizadorId}
                    promotores={organizadorConfig?.promotores || []}
                    value={formData._promotores || []}
                    onChange={val => setFormData(prev => ({ ...prev, _promotores: val }))}
                    titulo={isOrgFluxoCavalo ? '👤 Promotores (Leilão)' : '👤 Promotores'}
                    labelItem="promotor"
                  />
                )}
              </div>
            )}

          {/* Navigation */}
          <div className="flex justify-between items-center mt-8">
            <button
              type="button"
              onClick={prevStep}
              disabled={currentStep === 1}
              className={`px-6 py-2 rounded-lg font-medium ${
                currentStep === 1
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              Anterior
            </button>

            <div className="flex space-x-4">
              <button
                type="button"
                onClick={() => window.location.href = '/leiloes'}
                className="btn-secondary"
              >
                Cancelar
              </button>

              {currentStep < 4 ? (
                <button
                  type="button"
                  onClick={nextStep}
                  className="btn-primary"
                >
                  Próximo
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={loading}
                  className="btn-primary"
                >
                  {loading ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  ) : (
                    <Save className="w-4 h-4 mr-2" />
                  )}
                  Criar Leilão
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      <NovaComissaoModal
        isOpen={showNovaComissao}
        onClose={() => setShowNovaComissao(false)}
        onSave={handleSaveComissao}
        loading={loadingComissao}
      />

      <NovoLeiloeiroModal
        isOpen={showNovoLeiloeiro}
        onClose={() => setShowNovoLeiloeiro(false)}
        onSave={handleSaveLeiloeiro}
        loading={loadingLeiloeiro}
      />

      <EditarComissaoModal
        isOpen={showEditComissao}
        onClose={() => setShowEditComissao(false)}
        onSave={handleEditComissao}
        comissao={organizadorConfig?.comissoes?.find(c => c.id === parseInt(formData.comissao_padrao_id))}
        loading={loadingComissao}
      />

      <EditarLeiloeiroModal
        isOpen={showEditLeiloeiro}
        onClose={() => setShowEditLeiloeiro(false)}
        onSave={handleEditLeiloeiro}
        leiloeiro={organizadorConfig?.leiloeiros?.find(l => l.id === parseInt(formData.leiloeiro_id))}
        loading={loadingLeiloeiro}
      />

      <NovoMeioPagamentoModal
        isOpen={showNovoMeioPagamento}
        onClose={() => setShowNovoMeioPagamento(false)}
        onSave={handleSaveMeioPagamento}
      />
    </>
  )
}